/**
 * =============================================================================
 * AppNavPanel — Nível 2 da Navegação 2.0 (painel de submenus)
 * Versão: 1.7.0 (2026-10-10)
 *
 * Descrição:
 *   Renderiza a lista de submenus da categoria selecionada no rail. Os itens
 *   já chegam filtrados por permissão (ver useNavTree). Navegar não fecha o
 *   painel — ele apenas marca o item ativo, como no Pipedrive.
 *
 * CHANGELOG:
 *   v1.7.0 (2026-10-10)
 *     - Ordem de Serviço passa a reutilizar a árvore dinâmica do Kanban por categoria.
 *   v1.6.0 (2026-10-05)
 *     - Seção dinâmica de categorias passa a atender também Retenção e Jurídico
 *       conforme o tipo declarado no registro central de navegação.
 *   v1.5.0 (2026-10-05)
 *     - Suporte à seção especial do Kanban de Tarefas, com categorias ativas
 *       carregadas dinamicamente e acesso direto à categoria pela URL.
 *   v1.4.0 (2026-09-07)
 *     - Item ativo passa a ser apenas o de MAIOR especificidade. Antes, uma
 *       rota filha (ex.: /tenant/settings/exports) acendia também o item pai
 *       de prefixo menor (/tenant/settings).
 *   v1.3.0 (2026-08-25)
 *     - Suporte a títulos de SEÇÃO no painel via `item.sectionTitle`.
 *   v1.2.0 (2026-08-25)

 *     - `onNavigate` agora recebe a URL clicada, permitindo ao shell "fixar" a
 *       categoria de origem e não trocar o menu pai ao abrir a página.

 *   v1.1.0 (2026-08-23)
 *     - Suporte a GRUPOS de nível 3: itens com `children` viram um botão com
 *       setinha (chevron) que expande/recolhe os subitens (com ícones).
 *       Usado por "Inteligência de Dados" dentro de Configurações.
 *   v1.0.0 (2026-08-05) - Versão inicial.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { NavWorkspacesTree } from '@/components/navigation/NavWorkspacesTree';
import { NavTaskCategoriesTree } from '@/components/navigation/NavTaskCategoriesTree';
import type { NavCategory, NavItem } from '@/components/navigation/navRegistry';

interface AppNavPanelProps {
  category: NavCategory;
  /**
   * Callback após navegar (usado para fechar o Sheet no mobile e para o shell
   * "fixar" a categoria de origem do clique — ver AppShellSidebar v1.6.0).
   */
  onNavigate?: (url?: string) => void;
}

export function AppNavPanel({ category, onNavigate }: AppNavPanelProps) {
  const { t } = useTranslation('sidebar');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const currentUrl = `${pathname}${search}`;

  /**
   * Tamanho da correspondência de um item com a rota atual.
   * Retorna -1 quando não casa. Quanto maior, mais específico é o item.
   */
  const matchLength = (item: NavItem): number => {
    if (!item.url) return -1;
    const base = item.url.split('?')[0];
    if (currentUrl === item.url) return item.url.length;
    if (pathname === base || pathname.startsWith(`${base}/`)) return base.length;
    return -1;
  };

  /** Melhor correspondência (mais específica) entre todos os itens da categoria. */
  const melhorMatch = (() => {
    let melhor = -1;
    const percorrer = (itens: NavItem[]) => {
      for (const item of itens) {
        melhor = Math.max(melhor, matchLength(item));
        if (item.children?.length) percorrer(item.children);
      }
    };
    percorrer(category.children ?? []);
    return melhor;
  })();

  /**
   * Item ativo = casa com a rota atual COM a maior especificidade encontrada,
   * ou possui algum descendente ativo (para abrir/destacar o grupo).
   */
  const isItemActive = (item: NavItem): boolean => {
    if (melhorMatch >= 0 && matchLength(item) === melhorMatch) return true;
    return (item.children ?? []).some(isItemActive);
  };

  // Estado dos grupos abertos (nível 3).
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Abre automaticamente o grupo que contém a rota atual.
  useEffect(() => {
    const actives: Record<string, boolean> = {};
    for (const item of category.children ?? []) {
      if (item.children?.length && isItemActive(item)) actives[item.titleKey] = true;
    }
    if (Object.keys(actives).length) {
      setOpenGroups((prev) => ({ ...prev, ...actives }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, category.id]);

  /** Renderiza um item simples (link) — usado nos níveis 2 e 3. */
  const renderLink = (item: NavItem, depth: number) => {
    const Icon = item.icon;
    const active = isItemActive(item);

    return (
      <button
        key={`${item.url}-${item.titleKey}`}
        type="button"
        onClick={() => {
          if (!item.url) return;
          navigate(item.url);
          onNavigate?.(item.url);
        }}
        className={cn(
          'flex items-center gap-2 rounded-md py-2 pr-2 text-left text-sm transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          depth > 0 ? 'pl-7' : 'pl-2',
          active
            ? 'bg-sidebar-primary/10 font-medium text-sidebar-primary'
            : 'text-sidebar-foreground/85',
        )}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="truncate">{t(item.titleKey, item.defaultLabel)}</span>
      </button>
    );
  };

  /** Renderiza um grupo expansível (item com `children`). */
  const renderGroup = (item: NavItem) => {
    const Icon = item.icon;
    const isOpen = !!openGroups[item.titleKey];
    const active = isItemActive(item);

    return (
      <div key={`group-${item.titleKey}`} className="flex flex-col">
        <button
          type="button"
          onClick={() => setOpenGroups((prev) => ({ ...prev, [item.titleKey]: !prev[item.titleKey] }))}
          aria-expanded={isOpen}
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            active && !isOpen
              ? 'bg-sidebar-primary/10 font-medium text-sidebar-primary'
              : 'text-sidebar-foreground/85',
          )}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span className="flex-1 truncate">{t(item.titleKey, item.defaultLabel)}</span>
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 transition-transform duration-200',
              isOpen && 'rotate-90',
            )}
          />
        </button>

        {isOpen && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {item.children?.map((child) =>
              child.children?.length ? renderGroup(child) : renderLink(child, 1),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 w-[14.5rem] shrink-0 flex-col border-l border-sidebar-border/50">
      {/* Cabeçalho da categoria */}
      <div className="px-3 py-3">
        <h2 className="truncate text-sm font-semibold text-sidebar-foreground">
          {t(category.titleKey, category.defaultLabel)}
        </h2>
      </div>

      {/* Lista de submenus */}
      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3">
        {category.children?.map((item) => (
          <div key={`sec-${item.titleKey}`} className="contents">
            {/* v1.3.0: título de seção (não clicável) acima do item marcado */}
            {item.sectionTitle && (
              <span className="mt-3 px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {item.sectionTitle}
              </span>
            )}
            {item.children?.length ? renderGroup(item) : renderLink(item, 0)}
          </div>
        ))}


        {/* Seção especial: árvore de Workspaces → Pipelines do CRM */}
        {category.specialSection === 'crmWorkspaces' && (
          <>
            <Separator className="my-2 bg-sidebar-border opacity-50" />
            <span className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
              Workspaces
            </span>
            <NavWorkspacesTree onNavigate={onNavigate} />
          </>
        )}

        {/* Seção especial: atalhos do Kanban por categoria de Tarefas */}
        {category.specialSection === 'taskCategories' && (
          <>
            <Separator className="my-2 bg-sidebar-border opacity-50" />
            <span className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
              Kanban
            </span>
            <NavTaskCategoriesTree
              key={category.specialSectionTaskType ?? 'normal'}
              taskType={category.specialSectionTaskType ?? 'normal'}
              onNavigate={onNavigate}
            />
          </>
        )}

        {/* Seção especial: atalhos do Kanban por categoria de Ordem de Serviço */}
        {category.specialSection === 'serviceOrderCategories' && (
          <>
            <Separator className="my-2 bg-sidebar-border opacity-50" />
            <span className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
              Kanban
            </span>
            <NavTaskCategoriesTree
              key="service-orders"
              taskType="normal"
              serviceOrders
              onNavigate={onNavigate}
            />
          </>
        )}
      </nav>
    </div>
  );
}
